import React, { useState } from 'react'

import { OperationTypesMainFilters } from './OperationTypesMainFilters'
import { PriceMainFilters } from './PriceMainFilters'
import { PropertyTypesMainFilters } from './PropertyTypesMainFilters'
import { Autocomplete } from '@wiggot/search-components'
import { ESearchLevel } from '@wiggot/search-components/types-library/src'
import { useBodyScrollLock } from '@wiggot/web-components/hooks/ds/useBodyScrollLock'
import Portal from '@dd3tech/wiggui/Portal'
import Transition from '@dd3tech/wiggui/Transition'
import IconWiggot from '@dd3tech/wiggui/IconWiggot'
import GoogleIcon from '@dd3tech/wiggui/GoogleIcon'
import Button from '@dd3tech/wiggui/Button'
import { ILocation } from '@wiggot/search-components/state-library/src/stores/filterStore/types'

interface IProps {
  isOpen: boolean
  locations: ILocation[]
  onDeleteLocation: (id:string) => void
  onClose: () => void
  onClickSearch: () => void
  operationTypeData?: {
    value: string
    label: string
    icon: string
  }
  setAutocompleteValue: (value: string) => void
  onChangeOperationType: (value: string) => void
  onChangePropertyType: (value: { property_type: string[] }) => void
  onInputPricesChange: ({ key, value }: { key: string; value: number }) => void
  onChangeLocation: ({
    id,
    latitude,
    longitude,
    searchLevel,
    searchAddress
  }: {
    id:string
    latitude: number
    longitude: number
    searchLevel: ESearchLevel
    searchAddress: string
  }) => void
  pricesToShowCopy: string
  propertyTypeCopy: string
  autocompleteValue: string
  prices: {
    priceFrom?: number
    priceTo?: number
  }
  propertyTypes: string[]
}
export const MainFiltersModal = ({
  isOpen,
  operationTypeData,
  pricesToShowCopy,
  propertyTypeCopy,
  prices,
  propertyTypes,
  autocompleteValue,
  locations,
  onDeleteLocation,
  onChangePropertyType,
  onClose,
  onClickSearch,
  onChangeOperationType,
  onInputPricesChange,
  onChangeLocation,
  setAutocompleteValue
}: IProps) => {
  useBodyScrollLock(isOpen)
  const [openSections, setOpenSections] = useState({
    isOpenPrice: false,
    isOpenPropertyType: false,
    isOpenOperation: false,
    isOpenLocation: false
  })
  const buttonsData = [
    {
      placeHolder: 'Operación',
      content: operationTypeData
        ? operationTypeData.label
        : '¿Dónde quieres buscar?',
      icon: operationTypeData ? operationTypeData.icon : undefined,
      delay: 300,
      duration: 300,
      isOpen: openSections.isOpenOperation,
      onClick: () => handleSectionToggle('isOpenOperation'),
      editComponent: (
        <OperationTypesMainFilters
          onChangeOperationType={onChangeOperationType}
          operationType={operationTypeData?.value || ''}
        />
      )
    },
    {
      placeHolder: 'Dirección o lugar',
      delay: 320,
      duration: 300
    },
    {
      placeHolder: 'Precio',
      content: pricesToShowCopy,
      delay: 340,
      duration: 300,
      isOpen: openSections.isOpenPrice,
      onClick: () => handleSectionToggle('isOpenPrice'),
      editComponent: (
        <PriceMainFilters
          prices={prices}
          onInputPricesChange={onInputPricesChange}
        />
      )
    },
    {
      placeHolder: 'Tipo de propiedad',
      content: propertyTypeCopy,
      delay: 360,
      duration: 300,
      isOpen: openSections.isOpenPropertyType,
      onClick: () => handleSectionToggle('isOpenPropertyType'),
      editComponent: (
        <PropertyTypesMainFilters
          propertyTypes={propertyTypes}
          onChangePropertyType={onChangePropertyType}
        />
      )
    }
  ]

  const handleSectionToggle = (
    section: keyof typeof openSections,
    value?: boolean
  ) => {
    setOpenSections((prevState) => {
      const newState = Object.keys(prevState).reduce((acc, key) => {
        acc[key as keyof typeof openSections] = false
        return acc
      }, {} as typeof openSections)
      newState[section] = value ? value : !prevState[section]
      return newState
    })
  }
  const onFocus = () => {
    if (isOpen && autocompleteValue === '¿Dónde quieres buscar?') {
      setAutocompleteValue(' ')
    }
    handleSectionToggle('isOpenLocation', true)
  }
  const onBlur = () => {
    handleSectionToggle('isOpenLocation', false)
  }
  const isOpenLocation = openSections.isOpenLocation
  const handleSubmit = () => {
    onClickSearch()
    onClose()
  }
  return (
    <Portal>
      <Transition
        className="absolute top-0 left-0 w-full h-full z-10 bg-surface-strokes"
        animationEnd="slideOutUp"
        animationStart="slideInDown"
        delay={50}
        duration={300}
        show={isOpen}
      >
        <div className="flex flex-col justify-between h-full">
          <div className="flex flex-col">
            <Transition
              className="!flex items-center border-b border-surface-strokes px-6 py-2 bg-white w-full"
              delay={350}
              duration={300}
              show={isOpen}
            >
              <IconWiggot variant="black" />
              <div className="flex flex-1 justify-end items-center gap-2 ">
                <p className="text-secondary text-sm">Realizar búsqueda</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex justify-center items-center p-3"
                >
                  <GoogleIcon className="text-2xl font-bold" name="close" />
                </button>
              </div>
            </Transition>
            <div className="flex flex-col py-3 px-6 gap-6">
              {buttonsData.map(
                ({
                  placeHolder,
                  content,
                  icon,
                  delay,
                  duration,
                  isOpen: isOpenButton,
                  editComponent,
                  onClick
                }) => (
                  <div
                    className={`flex flex-col gap-2 ${
                      placeHolder === 'Dirección o lugar' ? 'z-[2]' : 'z-[1]'
                    }`}
                    key={placeHolder}
                  >
                    <Transition
                      animationStart="slideInRight"
                      delay={delay}
                      duration={duration}
                      show={isOpen}
                    >
                      {placeHolder === 'Dirección o lugar' ? (
                        <div
                          className="tour-search-step-1"
                          onFocusCapture={onFocus}
                          onBlurCapture={onBlur}
                        >
                          <Autocomplete
                            enableAutoFetch={false}
                            locations={locations}
                            onDeleteLocation={onDeleteLocation}
                            onSetValues={onChangeLocation}
                            placeholder="Dirección o lugar"
                            defaultFieldValue={autocompleteValue}
                            fieldContainerClassName="flex-1 max-w-full"
                            fieldClassName={`!border-none [&>svg:nth-of-type(1)]:hidden ${
                              isOpenLocation
                                ? '[&>svg:nth-of-type(2)]:opacity-1'
                                : '[&>svg:nth-of-type(2)]:opacity-0'
                            } [&>div]:!items-end [&>div]:!gap-1`}
                            valueClassName="!font-normal"
                            resultContainerClassName="!z-[20]"
                            containerClassName={`flex flex-col justify-center gap-1 rounded-full  active:bg-white active:shadow-[0px_3px_6px_0px_rgba(34,34,34,0.16)] bg-white ${
                              isOpenLocation
                                ? 'shadow-[0px_3px_6px_0px_rgba(34,34,34,0.16)]'
                                : ''
                            }`}
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={onClick}
                          className={`flex flex-col justify-center rounded-full px-4 py-[6px] bg-white border border-surface-strokes w-full ${
                            isOpenButton
                              ? 'shadow-[0px_3px_6px_0px_rgba(34,34,34,0.16)]'
                              : ''
                          }`}
                        >
                          <p className="text-secondary text-xs w-fit">
                            {placeHolder}
                          </p>
                          <p
                            className={`text-base text-primary gap-1 w-fit ${
                              icon
                                ? 'flex items-center'
                                : 'truncate max-w-[100%]'
                            }`}
                          >
                            {content}
                            {icon && (
                              <GoogleIcon
                                className="text-base leading-4"
                                name={icon}
                              />
                            )}
                          </p>
                        </button>
                      )}
                    </Transition>
                    {editComponent && (
                      <Transition duration={50} delay={100} show={isOpenButton}>
                        {editComponent}
                      </Transition>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
          <Transition
            onClick={handleSubmit}
            className="fixed bottom-0 !flex items-center border-t border-surface-strokes p-6 bg-white w-full"
            delay={350}
            duration={300}
            show={isOpen}
          >
            <Button fullWidth> Buscar</Button>
          </Transition>
        </div>
      </Transition>
    </Portal>
  )
}
